import { getSegment } from "./helpers.js";
import { copyAndMapObj } from "../../src/helpers";

/**
 * Map *form field names* to *segment data keys*
 * @type {Readonly<Object<string, string>>}
 */
var DB_ADDRESS_KEYS = {
  DemandBase_Country_Code__c: "country",
  DemandBase_City__c: "city",
  DemandBase_State__c: "state",
  db_street_address: "street",
  db_zip: "postal_code",
};

/** @type {Readonly<Object<string, string>>} */
var DB_REST_KEYS = {
  DemandBase_Annual_Sales__c: "db_annual_sales",
  db_b2b: "db_b2b",
  db_b2c: "db_b2c",
  DemandBase_Employee_Count__c: "db_employee_count",
  db_forbes_2000: "db_forbes_2000",
  DemandBase_Fortune_1000__c: "db_fortune_1000",
  db_ip: "db_ip",
  db_latitude: "db_latitude",
  db_longitude: "db_longitude",
  db_marketing_alias: "db_marketing_alias",
  db_primary_naics: "db_primary_naics",
  db_information_level: "db_information_level",
  db_session_id: "db_session_id",
  db_stock_ticker: "db_stock_ticker",
  DemandBase_Traffic__c: "db_traffic",
  DemandBase_Website__c: "db_web_site",
  DemandBase_Phone_Number__c: "db_phone",

  DemandBase_Country_Name__c: "country_name",
  db_data_source: "data_source",
  DemandBase_ID__c: "demandbase_sid",
  DemandBase_Employee_Range__c: "employee_range",
  mobile_country_code: "mobile_country_code",
  db_primary_sic: "primary_sic",
  DemandBase_Revenue_Range__c: "revenue_range",
  DemandBase_Sub_Industry__c: "sub_industry",
  DemandBase_Audience__c: "audience",

  Email: "email",
  DemandBase_Company_Name__c: "company_name",
  DemandBase_Primary_Industry__c: "industry",
};

/** @type {Readonly<Array<string>>} */
var DB_IDS = [
  "Email",
  "DemandBase_Company_Name__c",
  "DemandBase_Primary_Industry__c",
];

/**
 * @typedef {import('../helpers.js').FormAsObject} FormAsObject
 */

/**
 * Make a DemandBase object from formData
 * @arg {FormAsObject} formData
 * @return {Object<string, string> & Record<'address', Object<string, string>>}
 */
export function formDataToDemBaseTrackEventProps(formData) {
  /** @type {Object<string, string> & Record<'address', Object<string, string>>} */
  var to = copyAndMapObj(DB_REST_KEYS, formData, {});
  to.address = copyAndMapObj(DB_ADDRESS_KEYS, formData, {});
  return to;
}

/**
 * Check if the form object contains DemandBase data.
 * @arg {FormAsObject} formData
 * @return {boolean}
 */
export function hasDemBaseData(formData) {
  // See https://regex101.com/r/UrIgeD/ for explanation and tests
  var rx = new RegExp(/^\w*demand.*base/, "i"); // Compile as it is a constant
  return Object.keys(formData).some(function (it) {
    return rx.test(it);
  });
}

/**
 * Returns the complete list of ids allowed in controls in a DemandBase form.
 * @return {Readonly<Array<string>>}
 */
export function getAllDemBaseFieldIds() {
  var arr = Object.keys(DB_REST_KEYS);
  arr.push.apply(arr, Object.keys(DB_ADDRESS_KEYS));
  return arr;
}

/**
 * Returns the minimal list of ids allowed in controls in a DemandBase form.
 * @return {Readonly<Array<string>>}
 */
export function getKeyDemBaseFieldIds() {
  return DB_IDS;
}

/**
 * Sends a Segment track call with a 'DemandBase Event'.
 * See 'B2B Tracking Plan':
 * https://docs.google.com/spreadsheets/d/1joiekQ4SZdrDC7I7lDkO_MRFFKEByOw2Zs2uvCtCKpc/edit?usp=sharing
 *
 * @arg {object} properties
 * @return {void}
 */
export function trackDemBaseEvent(properties) {
  getSegment("track")("DemandBase Event", properties);
}

///////////////////////////////////////////////////////////////////////////
// Local Storage Data Management

const dbDataKey = "db_ip_data";
const dbDataTimestampKey = "db_ip_data_timestamp";

/**
 * Saves Demandbase data to localStorage
 * @param {FormAsObject} data - Demandbase data object
 */
export const saveDemandbaseDataToLocalStorage = (data) => {
  if (data && data["db_information_level"] === "Detailed") {
    localStorage.setItem(dbDataKey, JSON.stringify(data));
    localStorage.setItem(dbDataTimestampKey, Date.now().toString());
  }
};

/**
 * Removes expired Demandbase data from localStorage
 */
export const removeExpiredDemandbaseData = () => {
  const maxAge = 24 * 60 * 60 * 1000; // 1 day (ms)

  const storedTimestamp = localStorage.getItem(dbDataTimestampKey);

  const isValid =
    storedTimestamp && Date.now() < parseInt(storedTimestamp) + maxAge;
  if (!isValid) {
    localStorage.removeItem(dbDataKey);
    localStorage.removeItem(dbDataTimestampKey);
  }
};
